import { DateTime } from 'luxon';
import { List as ImList, Map as ImMap } from 'immutable';

import { chartOfAccountsSelectors } from 'companion-app-components/flux/chart-of-accounts';
import type { CashFlowTransaction, TransactionProps } from 'companion-app-components/flux/transactions/transactionsTypes';

// Used by TXList selector to create date groups for register display
export function groupTxnListByDate(
  txns: ImList<CashFlowTransaction>,
  featureFlags: ImMap<string, any>,
  showPendingSection: boolean = true,
) {
  const today = DateTime.local().startOf('day');
  const groupsOfTransactions = txns.groupBy((txn: CashFlowTransaction) => {
    const tDate = DateTime.fromISO(txn.postedOn).startOf('day');
    let ret: string;
    const isFuture = (tDate > today) && featureFlags.get('labelPendingAsFuture');
    const isPending = isFuture || (txn.state === 'PENDING' && featureFlags.get('manualArePending'));
    // Pending
    if (showPendingSection && isPending) {
      ret = featureFlags.get('labelPendingAsFuture') ? '9999-99:Future' : '9999-99:Pending';
      // within past 24 months
    } else if (tDate > today.minus({ months: 24 })) {
      ret = `${tDate.toFormat('yyyy-MM:MMMM yyyy')}`;
    } else {
      // group the rest by year
      ret = `${tDate.toFormat('yyyy-00:yyyy')}`;
    }
    return ret;
  });

  return groupsOfTransactions;
}

const expenseSectionTitle = 'Money Out';
const incomeSectionTitle = 'Money In';

// Used by TXList selector to create amount groups for register display
export function groupTxnListByAmount(txns: ImList<CashFlowTransaction>) {
  const groupsOfTransactions = txns.groupBy((txn: CashFlowTransaction) => {
    const amount: number = Number(txn.amount);
    let ret;
    switch (true) {
      case (amount >= -20) && (amount < 0):
        ret = `00:${expenseSectionTitle} 0 - 20`;
        break;
      case (amount >= -50) && (amount < -20):
        ret = `00:${expenseSectionTitle} 20 - 50`;
        break;
      case (amount >= -100) && (amount < -50):
        ret = `00:${expenseSectionTitle} 50 - 100`;
        break;
      case (amount >= -300) && (amount < -100):
        ret = `00:${expenseSectionTitle} 100 - 300`;
        break;
      case (amount >= -500) && (amount < -300):
        ret = `00:${expenseSectionTitle} 300 - 500`;
        break;
      case (amount >= -1000) && (amount < -500):
        ret = `00:${expenseSectionTitle} 500 - 1000`;
        break;
      case (amount >= -3000) && (amount < -1000):
        ret = `00:${expenseSectionTitle} 1000 - 3000`;
        break;
      case (amount >= -10000) && (amount < -3000):
        ret = `00:${expenseSectionTitle} 3000 - 10000`;
        break;
      case amount < -10000:
        ret = `00:${expenseSectionTitle} over 10000`;
        break;
      case (amount >= 0) && (amount < 100):
        ret = `00:${incomeSectionTitle} 0 - 100`;
        break;
      case (amount >= 100) && (amount < 500):
        ret = `00:${incomeSectionTitle} 100 - 500`;
        break;
      case (amount >= 500) && (amount < 1000):
        ret = `00:${incomeSectionTitle} 500 - 1000`;
        break;
      case (amount >= 1000) && (amount < 5000):
        ret = `00:${incomeSectionTitle} 1000 - 5000`;
        break;
      case amount >= 5000:
        ret = `00:${incomeSectionTitle} over 5000`;
        break;

      default:
        ret = '14:Other';
        break;
    }

    return ret;
  });

  return groupsOfTransactions;
}

// Used by TXList selector to create groups grouped alphabetically for register display
export function groupTxnListByString(txns: ImList<CashFlowTransaction>, field: string) {
  const groupsOfTransactions = txns.groupBy((txn: CashFlowTransaction) => {
    let startsWith: string;
    let strValue: string;
    switch (field) {
      case 'check':
        strValue = txn.check ? String(txn.check.number) : ' ';
        startsWith = strValue.charAt(0)?.toLowerCase();
        break;
      case 'category':
        strValue = txn.coa ? chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, true) : ' ';
        startsWith = strValue.charAt(0)?.toLowerCase();
        break;
      default:
        strValue = txn.get(field as keyof TransactionProps);
        if (strValue && strValue?.length > 0) {
          startsWith = strValue.charAt(0)?.toLowerCase();
        } else {
          startsWith = ' ';
        }
    }
    let ret: string;
    strValue = strValue || ' ';
    switch (true) {
      case strValue?.toLowerCase() === 'future':
        ret = '00:FUTURE';
        break;
      case strValue?.toLowerCase() === 'pending':
        ret = '00:Uncleared';
        break;
      case strValue?.toLowerCase() === 'cleared':
        ret = '00:Cleared';
        break;
      case strValue?.toLowerCase() === 'reconciled':
        ret = '00:Reconciled';
        break;
      case startsWith >= 'a' && startsWith <= 'd':
        ret = '00:A - D';
        break;
      case startsWith >= 'e' && startsWith <= 'h':
        ret = '01:E - H';
        break;
      case startsWith >= 'i' && startsWith <= 'l':
        ret = '02:I - L';
        break;
      case startsWith >= 'm' && startsWith <= 'p':
        ret = '03:M - P';
        break;
      case startsWith >= 'q' && startsWith <= 't':
        ret = '04:Q - T';
        break;
      case startsWith >= 'u' && startsWith <= 'z':
        ret = '05:U - Z';
        break;
      case startsWith >= '0' && startsWith <= '9':
        ret = '06:Numbers';
        break;

      default:
        ret = '07:Other';
        break;
    }
    return ret;
  });

  return groupsOfTransactions;
}
